import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { tenantStorageKey } from '../shared/helpers/tenant';

// Room passwords belong to a tenant; see `tenantStorageKey`.
const STORAGE_KEY = tenantStorageKey('andorRoomPasswords');
/**
 * Shortest password a user may choose for a new encrypted room, counted in
 * characters (code points). The password is the room's only secret and can be
 * guessed offline from captured traffic or a database copy, so it sits above
 * usual login minimums. Not applied when joining or to invite-link passwords.
 */
export const MIN_ROOM_PASSWORD_LENGTH = 12;
// Generated passwords (62-symbol alphabet, ~71 bits) meet the minimum.
const PASSWORD_LENGTH = MIN_ROOM_PASSWORD_LENGTH;
// URL-fragment-safe characters; no need for percent-encoding when embedded in
// a `#p=` invite link.
const PASSWORD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** Why the server refused a join: no password sent, or a wrong one. */
export type RoomPasswordRejection = 'required' | 'invalid';

/**
 * Per-room cipher-password registry of this browser, shared by its tabs.
 *
 * Kept in `localStorage`, so a room's creator can still join it later without
 * the invite link. Holds passwords either entered manually by the user,
 * generated locally when a brand-new room is created with end-to-end encryption
 * enabled, or recovered from the `#p=...` fragment of an invite link. A password
 * stays until it proves useless: the server refuses it ({@link reject}), a join
 * reports the room has none, or the user turns encryption off for the room.
 * Rooms missing from the room list are not pruned: hidden rooms are never
 * listed.
 *
 * Tabs stay in sync: a write applies its change to the latest saved map, and a
 * `storage` event adopts another tab's write, so no tab overwrites a password
 * another one saved after it loaded. Only the room of this tab's call keeps the
 * key the call uses ({@link pin}).
 *
 * Backed by a signal-wrapped `ReadonlyMap`, so reading `has()` / `get()` from
 * inside a `computed()` or `effect()` automatically subscribes to mutations —
 * no separate reactivity hook needed.
 *
 * Also records, in memory only, rooms whose join the server refused for a
 * missing or wrong password ({@link rejection}), so the pre-join screens can
 * ask for it; storing a new password for the room clears that record. Such a
 * room stays known as password-checked for the tab ({@link requiresPassword}),
 * even when no room list shows it (hidden rooms, typed ids).
 *
 * Edge case: two tabs creating the same brand-new room at the same time will
 * each generate their own password locally. In a password-checked room the
 * second tab's join is refused as a wrong password instead of silently
 * failing to decrypt.
 */
@Injectable({ providedIn: 'root' })
export class RoomPasswordStore {
  private readonly _passwords = signal<ReadonlyMap<string, string>>(new Map());
  private readonly _rejections = signal<ReadonlyMap<string, RoomPasswordRejection>>(new Map());
  private readonly _checkedRooms = signal<ReadonlySet<string>>(new Set());
  // The room of this tab's call and the password its cipher uses (`null`:
  // none); see `pin`.
  private readonly _pinned = signal<{ roomId: string; password: string | null } | null>(null);

  constructor() {
    this._hydrate();
    // Another tab saved or dropped a password (a tab gets no event for its own
    // writes). A `null` key means that tab cleared the whole storage.
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      this._passwords.set(parsePasswords(event.newValue) ?? new Map());
    };
    window.addEventListener('storage', onStorage);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', onStorage));
  }

  get(roomId: string): string | undefined {
    const pinned = this._pinned();
    if (pinned?.roomId === roomId) return pinned.password ?? undefined;
    return this._passwords().get(roomId);
  }

  has(roomId: string): boolean {
    return this.get(roomId) !== undefined;
  }

  set(roomId: string, password: string): void {
    if (!roomId || !password) return;
    this._clearRejection(roomId);
    this._repin(roomId, password);
    if (this._passwords().get(roomId) === password) return;
    this._update((passwords) => passwords.set(roomId, password));
  }

  /**
   * Keep `roomId` at `password` (`null`: no password) while this tab's call is
   * in it, whatever other tabs save meanwhile, so the call's media keys, room
   * files and invite links stay on the key its cipher was derived from. This
   * tab's own `set()` / `remove()` still apply. A tab holds one call, so a pin
   * replaces the previous one.
   */
  pin(roomId: string, password: string | null): void {
    this._pinned.set({ roomId, password });
  }

  /** Release the pin when the call ends. */
  unpin(): void {
    this._pinned.set(null);
  }

  /** Why the server last refused to let us into `roomId`, if it did. */
  rejection(roomId: string): RoomPasswordRejection | undefined {
    return this._rejections().get(roomId);
  }

  /**
   * Record that the server refused the join of `roomId`: `'required'` when we
   * sent no password, `'invalid'` when ours was wrong — which also drops it, so
   * the pre-join screens ask again instead of showing it as saved.
   */
  reject(roomId: string, reason: RoomPasswordRejection): void {
    if (!roomId) return;
    if (reason === 'invalid') this.remove(roomId);
    const next = new Map(this._rejections());
    next.set(roomId, reason);
    this._rejections.set(next);
    if (!this._checkedRooms().has(roomId)) {
      this._checkedRooms.set(new Set(this._checkedRooms()).add(roomId));
    }
  }

  /**
   * Whether the server refused a join of `roomId` for its password during this
   * tab's lifetime — so it is a password-checked room, listed or not.
   */
  requiresPassword(roomId: string): boolean {
    return this._checkedRooms().has(roomId);
  }

  remove(roomId: string): void {
    this._repin(roomId, null);
    if (!this._passwords().has(roomId)) return;
    this._update((passwords) => passwords.delete(roomId));
  }

  generate(): string {
    const out = new Array<string>(PASSWORD_LENGTH);
    const buf = new Uint32Array(PASSWORD_LENGTH);
    crypto.getRandomValues(buf);
    for (let i = 0; i < PASSWORD_LENGTH; i++) {
      out[i] = PASSWORD_ALPHABET[buf[i] % PASSWORD_ALPHABET.length];
    }
    return out.join('');
  }

  private _hydrate(): void {
    const passwords = this._readSaved() ?? new Map<string, string>();
    // Before moving to `localStorage`, each tab kept its passwords in
    // `sessionStorage` under the same key. A tab open across the update
    // carries its own over, without replacing one another tab already saved.
    const legacy = readLegacyPasswords();
    if (legacy) {
      for (const [roomId, password] of legacy) {
        if (!passwords.has(roomId)) passwords.set(roomId, password);
      }
      if (this._save(passwords)) removeLegacyPasswords();
    }
    this._passwords.set(passwords);
  }

  /**
   * Apply `change` to the latest saved map rather than this tab's copy, so a
   * password another tab saved meanwhile survives, then save and publish it.
   */
  private _update(change: (passwords: Map<string, string>) => void): void {
    const next = new Map(this._readSaved() ?? this._passwords());
    change(next);
    this._passwords.set(next);
    this._save(next);
  }

  /** The saved map; `null` when storage is unavailable or the payload corrupt. */
  private _readSaved(): Map<string, string> | null {
    try {
      return parsePasswords(localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private _save(passwords: ReadonlyMap<string, string>): boolean {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(passwords)));
      return true;
    } catch {
      // localStorage can throw under quota / private-mode constraints;
      // in-memory map still works for the lifetime of the page.
      return false;
    }
  }

  private _repin(roomId: string, password: string | null): void {
    if (this._pinned()?.roomId === roomId) this._pinned.set({ roomId, password });
  }

  private _clearRejection(roomId: string): void {
    const current = this._rejections();
    if (!current.has(roomId)) return;
    const next = new Map(current);
    next.delete(roomId);
    this._rejections.set(next);
  }
}

/** A saved `{ roomId: password }` payload as a map; `null` when corrupt. */
function parsePasswords(raw: string | null): Map<string, string> | null {
  const passwords = new Map<string, string>();
  if (!raw) return passwords;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  for (const [roomId, password] of Object.entries(parsed)) {
    if (typeof password === 'string') passwords.set(roomId, password);
  }
  return passwords;
}

/** This tab's pre-`localStorage` passwords; `null` when it holds none. */
function readLegacyPasswords(): Map<string, string> | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    // A corrupt payload is dropped like an empty one.
    return raw === null ? null : (parsePasswords(raw) ?? new Map());
  } catch {
    return null;
  }
}

function removeLegacyPasswords(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Unavailable storage holds nothing to remove.
  }
}
